import React, { Component } from 'react';
import { Alert, Box, Button, CircularProgress, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import Api from '../../services/api';
import RateLimitSettingsService from '../../services/rate-limit-settings.service';
import GroupedRichAutoComplete, { FREE_OPTION_ID } from '../auto-complete/autocomplete-grouped-rich';
import { t } from '../usage/usage-i18n';

// ---------------------------------------------------------------------------
// Bloco "Limite de requisições deste ambiente" da tela de configuração (aba geral).
// SÓ o administrador global da Mexx (id 1) vê e altera: o servidor manda canEdit e confere de novo ao gravar; para qualquer
// outro usuário o bloco não aparece. Cada linha é uma EXCEÇÃO deste cliente para um endpoint (requisições por janela), que vence
// o limite padrão do código. Sem linhas = tudo no padrão. Vale em até 1 minuto em todas as APIs, sem deploy.
//
// Nome da exceção = "Api.Entidade.Metodo" (2Do.User.AuthToken, CalledService.Called.Create): a API faz parte do nome, então o mesmo
// Entidade.Metodo em duas APIs tem limites separados.
// Campo de endpoint = GroupedRichAutoComplete (components/auto-complete/autocomplete-grouped-rich.js): lista larga, agrupada por API
// com cabeçalho fixo, cada opção com Entidade.Metodo em destaque, o nome completo embaixo e o limite padrão num selo à direita.
// Digita para pesquisar em todas as APIs de uma vez. A 2DO lista os próprios endpoints com limite e busca os das outras APIs do
// cliente QUANDO A TELA ABRE (junto com as exceções), DEPOIS DE SALVAR e no botão de recarregar, que não mexe nas linhas em edição.
// Escolher um endpoint SEMPRE carrega o padrão do código dele nos dois números. A última opção da lista, sempre visível, é
// "usar o que digitei": abre o campo de nome digitado já preenchido (API que não respondeu ou endpoint não listado).
// Salva por conta própria (PUT api/security/rate-limit/settings), fora do botão Salvar da tela.
// ---------------------------------------------------------------------------

const REST_RE = /^[A-Za-z0-9_\-:]+(\.[A-Za-z0-9_\-:]+)*$/;

export default class RateLimitSettingsBlock extends Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, saving: false, reloading: false, error: null, settings: null, apis: [], rows: [] };
    this.save = this.save.bind(this);
    this.addRow = this.addRow.bind(this);
    this.reload = this.reload.bind(this);
  }

  componentDidMount() { this.load(); }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }
  notify(v, m) { if (this.props.onNotify) this.props.onNotify(v, m); }

  // Linha vinda do servidor: "custom" (campo de digitar) quando o nome gravado não está no catálogo carregado.
  toRows(overrides, apis) { return (overrides || []).map((o) => ({ ...o, custom: !this.findIn(apis, o.endpoint) })); }

  load() {
    RateLimitSettingsService.settings()
      .then((s) => this.setState({ loading: false, settings: s, apis: s.apis, rows: this.toRows(s.overrides, s.apis) }))
      .catch(() => this.setState({ loading: false, settings: null })); // sem permissão ou API antiga: o bloco simplesmente não aparece
  }

  // Botão recarregar: busca o catálogo de novo nas APIs do cliente. Não toca nos números em edição; linha digitada cujo nome
  // passou a existir no catálogo volta a aparecer selecionada no select.
  reload() {
    this.setState({ reloading: true });
    RateLimitSettingsService.catalog()
      .then((apis) => this.setState((st) => ({ reloading: false, apis, rows: st.rows.map((r) => (r.custom && this.findIn(apis, r.endpoint) ? { ...r, custom: false, endpoint: this.findIn(apis, r.endpoint).endpoint } : r)) })))
      .catch((err) => { this.setState({ reloading: false }); this.notify('error', err.message); });
  }

  findIn(apis, name) {
    const key = String(name || '').trim().toLowerCase();
    if (!key) return null;
    for (let i = 0; i < (apis || []).length; i += 1) {
      const found = (apis[i].endpoints || []).find((e) => e.endpoint.toLowerCase() === key);
      if (found) return found;
    }
    return null;
  }

  find(name) { return this.findIn(this.state.apis, name); }

  patchRow(index, p) { this.setState((st) => ({ rows: st.rows.map((r, i) => (i === index ? { ...r, ...p } : r)), error: null })); }

  // Opções agrupadas por API, na ordem em que o servidor devolve (2Do, CalledService, Reports, 2Talk). API sem endpoints entra
  // com uma linha informativa desabilitada, para o grupo aparecer e dizer por quê.
  buildOptions() {
    const options = [];
    (this.state.apis || []).forEach((a) => {
      if (!a.endpoints || a.endpoints.length === 0) {
        options.push({ id: `__empty_${a.api}`, group: a.api, label: '', disabled: true,
          primary: a.ok ? this.tr('security.ratelimit.group.none', 'Esta API não tem endpoint com limite.') : this.tr('security.ratelimit.group.down', 'Não carregou. Recarregue ou use a opção de digitar, no fim da lista.') });
        return;
      }
      a.endpoints.forEach((e) => options.push({
        // 2ª linha = rota HTTP (diz qual endpoint é, mesmo quando o nome é um prefixo próprio); sem rota (API antiga), o nome completo.
        id: e.endpoint, group: a.api, label: e.endpoint, primary: e.name, secondary: this.routeText(e) || e.endpoint,
        badge: `${e.defaultMaxRequests} / ${e.defaultWindowSeconds} s${e.anonymous ? ` · ${this.tr('security.ratelimit.badge.anonymous', 'sem login')}` : ''}`,
        badgeColor: e.anonymous ? '#ef6c00' : undefined,
      }));
    });
    return options;
  }

  // "POST /api/called/client/external" (ou só a rota, ou vazio quando a API ainda não informa).
  routeText(e) { return e && e.route ? `${e.method ? `${e.method} ` : ''}${e.route}` : ''; }

  // Texto à direita do cabeçalho de cada grupo: quantidade de endpoints, ou o aviso de que a API não carregou.
  buildGroupInfo() {
    const info = {};
    (this.state.apis || []).forEach((a) => {
      info[a.api] = a.ok
        ? { note: `${a.endpoints.length} ${this.tr('security.ratelimit.group.count', 'endpoint(s)')}` }
        : { note: this.tr('security.ratelimit.apiDown', 'não carregou'), noteColor: '#c62828' };
    });
    return info;
  }

  // Escolha no autocomplete: endpoint do catálogo, "usar o que digitei" (vira linha digitada) ou limpar.
  changeEndpoint(index, option) {
    if (!option) { this.changeSelect(index, ''); return; }
    if (option.id === FREE_OPTION_ID) { this.patchRow(index, { custom: true, endpoint: option.inputValue || '' }); return; }
    this.changeSelect(index, option.id);
  }

  // Escolher no autocomplete: SEMPRE carrega o padrão do código do endpoint escolhido (é o ponto de partida para ajustar).
  changeSelect(index, value) {
    const known = this.find(value);
    if (!known) { this.patchRow(index, { custom: false, endpoint: '', maxRequests: '', windowSeconds: '' }); return; }
    this.patchRow(index, { custom: false, endpoint: known.endpoint, maxRequests: known.defaultMaxRequests, windowSeconds: known.defaultWindowSeconds });
  }

  addRow() {
    const { rows, settings } = this.state;
    if (rows.length >= settings.limits.maxOverrides) { this.setState({ error: this.tr('security.ratelimit.err.max', 'Limite de exceções atingido.') }); return; }
    this.setState({ rows: [...rows, { endpoint: '', maxRequests: '', windowSeconds: '', ipMaxRequests: null, custom: false }], error: null });
  }

  removeRow(index) { this.setState((st) => ({ rows: st.rows.filter((r, i) => i !== index), error: null })); }

  validName(name) {
    const { settings } = this.state;
    const dot = name.indexOf('.');
    if (dot <= 0 || dot >= name.length - 1) return false;
    const api = name.substring(0, dot).toLowerCase();
    if (!(settings.apiLabels || []).some((l) => String(l).toLowerCase() === api)) return false;
    return REST_RE.test(name.substring(dot + 1));
  }

  // Mesmas cercas da API, antecipadas na tela (a API valida de novo).
  validate() {
    const { rows, settings } = this.state; const lim = settings.limits;
    const seen = {};
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i]; const name = String(r.endpoint || '').trim();
      if (!this.validName(name)) return this.tr('security.ratelimit.err.name', 'Endpoint inválido na linha {n}: use Api.Entidade.Metodo (ex.: CalledService.Called.Create). APIs: {apis}.').replace('{n}', i + 1).replace('{apis}', (settings.apiLabels || []).join(', '));
      if (seen[name.toLowerCase()]) return this.tr('security.ratelimit.err.dup', 'Endpoint repetido: {name}.').replace('{name}', name);
      seen[name.toLowerCase()] = true;
      const max = Number(r.maxRequests); const win = Number(r.windowSeconds);
      if (!Number.isInteger(max) || max < 1 || max > lim.maxRequests) return `${name}: ${this.tr('security.ratelimit.err.requests', 'requisições entre 1 e {max}.').replace('{max}', lim.maxRequests)}`;
      if (!Number.isInteger(win) || win < 1 || win > lim.maxWindowSeconds) return `${name}: ${this.tr('security.ratelimit.err.window', 'janela entre 1 e {max} segundos.').replace('{max}', lim.maxWindowSeconds)}`;
    }
    return null;
  }

  save() {
    const error = this.validate();
    if (error) { this.setState({ error }); return; }
    this.setState({ saving: true, error: null });
    RateLimitSettingsService.saveSettings(this.state.rows)
      .then((s) => { this.setState({ saving: false, settings: s, apis: s.apis, rows: this.toRows(s.overrides, s.apis) }); this.notify('success', this.tr('security.ratelimit.saved', 'Limite de requisições salvo. Vale em até 1 minuto em todas as APIs.')); })
      .catch((err) => { Api.kickoff(err); this.setState({ saving: false, error: err.message }); this.notify('error', err.message); });
  }

  renderRow(row, index) {
    const { settings, reloading } = this.state;
    const options = this.buildOptions();
    const known = row.custom ? null : this.find(row.endpoint);
    const selected = known ? options.find((o) => o.id === known.endpoint) || null : null;
    let hint = ' ';
    if (known) hint = `${this.routeText(known) ? `${this.routeText(known)} · ` : ''}${this.tr('security.ratelimit.default', 'Padrão do código')}: ${known.defaultMaxRequests} / ${known.defaultWindowSeconds} s${known.anonymous ? ` · ${this.tr('security.ratelimit.anonymous', 'endpoint sem login: teto de {n}× o padrão').replace('{n}', settings.limits.anonymousCeiling)}` : ''}`;
    return (
      <Grid item xs={12} key={index}>
        <Box style={{ border: '1px solid #eee', borderRadius: 8, padding: '10px 12px', borderLeft: '4px solid #8BC34A' }}>
          <Grid container spacing={1} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <GroupedRichAutoComplete label={this.tr('security.ratelimit.endpoint', 'Endpoint (Api.Entidade.Metodo)')} size="small" options={options} value={selected}
                    groupInfo={this.buildGroupInfo()} popupMinWidth={620} listMaxHeight={440} loading={reloading}
                    placeholder={row.custom ? this.tr('security.ratelimit.placeholder.custom', 'Nome digitado abaixo. Pesquise aqui para trocar por um da lista') : this.tr('security.ratelimit.placeholder', 'Digite para pesquisar: API, entidade ou método')}
                    noOptionsText={this.tr('security.ratelimit.noOptions', 'Nenhum endpoint encontrado.')}
                    freeGroupLabel={this.tr('security.ratelimit.free.group', 'Não está na lista?')}
                    freeOption={{
                      primary: (typed) => (typed ? this.tr('security.ratelimit.free.use', 'Usar "{text}"').replace('{text}', typed) : this.tr('security.ratelimit.other', 'Digitar o nome (Api.Entidade.Metodo)')),
                      secondary: this.tr('security.ratelimit.free.help', 'Para API que não carregou ou endpoint fora da lista. Formato: Api.Entidade.Metodo'),
                    }}
                    onChange={(option) => this.changeEndpoint(index, option)} helperText={hint} />
                </Box>
                <Tooltip title={this.tr('security.ratelimit.reload', 'Buscar de novo os endpoints nas APIs deste ambiente')}>
                  <span>
                    <IconButton size="small" onClick={this.reload} disabled={reloading} style={{ marginTop: 4 }}>
                      {reloading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" type="number" variant="outlined" label={this.tr('security.ratelimit.requests', 'Requisições')} value={row.maxRequests} InputLabelProps={{ shrink: true }}
                inputProps={{ min: 1, max: settings.limits.maxRequests }} onChange={(e) => this.patchRow(index, { maxRequests: e.target.value })} />
            </Grid>
            <Grid item xs={5} md={3}>
              <TextField fullWidth size="small" type="number" variant="outlined" label={this.tr('security.ratelimit.window', 'Janela (segundos)')} value={row.windowSeconds} InputLabelProps={{ shrink: true }}
                inputProps={{ min: 1, max: settings.limits.maxWindowSeconds }} onChange={(e) => this.patchRow(index, { windowSeconds: e.target.value })} />
            </Grid>
            <Grid item xs={1} md={1} style={{ textAlign: 'right' }}>
              <Tooltip title={this.tr('security.ratelimit.remove', 'Remover exceção (volta ao padrão do código)')}><IconButton size="small" onClick={() => this.removeRow(index)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
            </Grid>
            {row.custom && (
              <Grid item xs={12} md={5}>
                <TextField fullWidth size="small" variant="outlined" label={this.tr('security.ratelimit.custom', 'Nome digitado (Api.Entidade.Metodo)')} value={row.endpoint} InputLabelProps={{ shrink: true }}
                  placeholder="CalledService.Called.Create" inputProps={{ maxLength: 120 }} onChange={(e) => this.patchRow(index, { endpoint: e.target.value })}
                  helperText={this.tr('security.ratelimit.custom.help', 'Confira o nome exato: nome que não existe fica gravado e não tem efeito.')} />
              </Grid>
            )}
          </Grid>
        </Box>
      </Grid>
    );
  }

  render() {
    const { loading, saving, reloading, error, settings, rows, apis } = this.state;
    // Só o administrador global da Mexx (id 1). Para os demais (ou API sem a rota) não renderiza nada.
    if (loading || !settings || !settings.canEdit) return null;
    const failed = apis.filter((a) => !a.ok);
    return (
      <Box style={{ border: '1px solid #e0e0e0', borderRadius: 12, padding: 16, marginTop: 8 }}>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{this.tr('security.ratelimit.title', 'Limite de requisições deste ambiente')}</Typography>
        <Typography variant="caption" style={{ color: '#666', display: 'block', marginBottom: 12 }}>
          {this.tr('security.ratelimit.intro', 'Exceções deste cliente ao limite padrão de cada endpoint. Sem exceção cadastrada vale o limite do código. Só o administrador global da Mexx vê e altera este bloco. A mudança vale em até 1 minuto em todas as APIs, sem reinício.')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="info" action={(
              <Button size="small" color="inherit" onClick={this.reload} disabled={reloading} startIcon={reloading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon fontSize="small" />}>
                {this.tr('security.ratelimit.reloadBtn', 'Recarregar endpoints')}
              </Button>
            )}>
              {this.tr('security.ratelimit.naming', 'O nome de cada exceção segue a lógica Api.Entidade.Metodo, por exemplo CalledService.Called.Create ou 2Do.User.AuthToken. A API faz parte do nome: o mesmo Entidade.Metodo em duas APIs tem limites separados.')}
              {' '}
              {this.tr('security.ratelimit.naming.other', 'Digite no campo para pesquisar em todas as APIs. Se uma API estiver sem endpoints na lista, recarregue; se continuar, escolha "Outro" e digite o nome nesse mesmo formato, que funciona do mesmo jeito.')}
              {' '}
              {`${this.tr('security.ratelimit.apis', 'APIs')}: ${(settings.apiLabels || []).join(', ')}.`}
            </Alert>
          </Grid>
          {failed.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                {this.tr('security.ratelimit.failed', 'Não foi possível carregar os endpoints de:')}{' '}
                {failed.map((a) => `${a.api} (${a.error || '?'})`).join('; ')}
              </Alert>
            </Grid>
          )}
          {settings.readError && <Grid item xs={12}><Alert severity="warning">{settings.readError}</Alert></Grid>}
          {rows.length === 0 && (
            <Grid item xs={12}><Alert severity="success">{this.tr('security.ratelimit.empty', 'Nenhuma exceção: todos os endpoints deste ambiente usam o limite padrão do código.')}</Alert></Grid>
          )}
          {rows.map((r, i) => this.renderRow(r, i))}
          {error && <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>}
          <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" color="primary" onClick={this.addRow} disabled={saving}>{this.tr('security.ratelimit.add', 'Adicionar exceção')}</Button>
            <Button variant="contained" color="primary" onClick={this.save} disabled={saving} startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>{this.tr('security.ratelimit.save', 'Salvar limite de requisições')}</Button>
          </Grid>
        </Grid>
      </Box>
    );
  }
}
